import { useEffect, useRef, useState } from 'react';
import { Board } from '@/chess/board';
import { Button, Caption } from '@/ui';
import type { InContextLine } from '../inContextMapping';
import { buildAnimationSteps, type AnimationStep } from './buildAnimationSteps';

/**
 * InContextIntro — anime les 10 premiers coups d'une ouverture cohérente
 * avec le thème tactique, avant que Léon ne reprenne le contrôle pour
 * résoudre la tactique (MVP2.6 Gap 2 V0).
 *
 * Objectif pédagogique : forcer le passage middlegame → tactique (cf.
 * PRODUCT.md §4.2). Léon doit s'habituer à voir une position tactique
 * "en sortie de plan d'ouverture", pas en isolation. C'est une cause
 * probable du gap Lichess Puzzle 2144 vs Blitz 1949 (puzzle isolé ≠
 * tactique en cours de partie).
 *
 * Comportement :
 *  - Affiche le board en position initiale, joue les `line.moves` un par
 *    un avec un délai `stepDelayMs` (par défaut 1500 ms = 1.5 s/coup).
 *  - Pendant l'animation, le board n'est pas interactif.
 *  - À la fin, affiche un bouton « À toi » qui appelle `onReady()`. Le
 *    parent passe alors le mode du drill normal sur la FEN finale.
 *  - `prefers-reduced-motion` : l'animation reste mais le bouton « Passer »
 *    permet de zapper directement à la position finale.
 *
 * Notes :
 *  - Le coup adversaire pendant le drill suivant ne passe PAS par ce
 *    composant — le mapping fournit juste la FEN de départ à TacticsDrillPage,
 *    qui ré-utilise sa state machine normale ensuite.
 *  - On utilise chess.js pour construire la séquence FEN intermédiaire
 *    (1 par coup) pour pouvoir rejouer l'animation sur le composant Board.
 */

export interface InContextIntroProps {
  line: InContextLine;
  /**
   * Orientation du board pendant l'animation. Idéalement = `side_to_move`
   * du puzzle (Léon voit l'animation depuis sa propre couleur, comme s'il
   * la jouait).
   */
  orientation: 'white' | 'black';
  /**
   * Callback déclenché quand Léon clique « À toi » (ou auto-trigger si
   * `autoAdvance=true` et l'animation est terminée).
   */
  onReady: () => void;
  /**
   * Délai entre 2 coups en millisecondes. Par défaut 1500 ms. Plus court
   * = plus rythmé mais moins lisible. Plus long = pédagogique mais peut
   * lasser Léon.
   */
  stepDelayMs?: number;
}

export function InContextIntro({
  line,
  orientation,
  onReady,
  stepDelayMs = 1500,
}: InContextIntroProps): JSX.Element {
  const steps = useRef<AnimationStep[]>(buildAnimationSteps(line));
  const [stepIdx, setStepIdx] = useState<number>(0);
  const [skipped, setSkipped] = useState<boolean>(false);

  // Animation : avance d'un step tant qu'on n'a pas atteint la fin
  // et qu'on n'est pas en mode "skipped".
  useEffect(() => {
    if (skipped) return undefined;
    if (stepIdx >= steps.current.length - 1) return undefined;
    const id = setTimeout(() => {
      setStepIdx((i) => i + 1);
    }, stepDelayMs);
    return () => clearTimeout(id);
  }, [stepIdx, skipped, stepDelayMs]);

  const current = steps.current[stepIdx]!;
  const isFinished = skipped || stepIdx >= steps.current.length - 1;

  function handleSkip(): void {
    setSkipped(true);
    setStepIdx(steps.current.length - 1);
  }

  return (
    <section
      data-testid="in-context-intro"
      className="flex flex-col gap-4 p-4 rounded-lg glass glass-cyan"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <Caption color="cyan">Mise en contexte</Caption>
          <h3 className="font-display text-heading-3 text-paper-50">
            {line.openingName}
          </h3>
          <p className="text-body-sm text-ink-300 italic leading-snug">
            La tactique arrive après 10 coups d&apos;ouverture. Regarde, puis joue.
          </p>
        </div>
        <span className="font-mono text-[11px] text-ink-300 tabular-nums" data-testid="in-context-step">
          {current.moveNumber}/{steps.current.length - 1}
        </span>
      </header>

      <Board
        fen={current.fen}
        orientation={orientation}
        interactive={false}
        {...(current.lastMove ? { lastMove: current.lastMove } : {})}
      />

      <footer className="flex items-center gap-3 flex-wrap">
        {!isFinished && (
          <span data-testid="in-context-skip">
            <Button variant="ghost" onClick={handleSkip}>
              Passer l&apos;intro
            </Button>
          </span>
        )}
        {isFinished && (
          <span data-testid="in-context-ready">
            <Button variant="primary" accent="cyan" onClick={onReady}>
              À toi de jouer →
            </Button>
          </span>
        )}
        <span className="ml-auto font-mono text-[10px] text-ink-300">{line.source}</span>
      </footer>
    </section>
  );
}
